/**
 * Social Proof Service
 *
 * Provides social proof data to build trust and guide customers.
 * Currently uses mock data - will be replaced with real analytics later.
 */

export interface PopularContainer {
  materialType: string;
  popularSize: string;
  percentage: number; // 0-100, percentage of customers who chose this size
  bookingsThisMonth: number;
}

export interface RegionalActivity {
  zipCode: string;
  ordersThisMonth: number;
  trending: boolean; // True if orders increased >20% vs last month
}

export interface ProviderTrust {
  providerId: string;
  rating: number; // 1-5
  totalReviews: number;
  verified: boolean;
  yearsInBusiness: number;
  certificationsCount: number;
}

export interface MaterialUsageStats {
  materialType: string;
  usageCount: number; // How many times this material was selected
  percentageOfTotal: number; // Percentage of all bookings
  trending: boolean;
}

export interface SocialProofSettings {
  enabled: boolean;
  showPopularityBadges: boolean;
  showRegionalActivity: boolean;
  showProviderTrust: boolean;
  minimumOrdersThreshold: number; // Don't show "X orders" if below this
  minimumPercentageThreshold: number; // Don't show percentage if below this
}

/**
 * Mock data - Most popular container sizes by material type
 * Based on typical industry patterns
 */
const POPULAR_CONTAINERS: PopularContainer[] = [
  // Heavy materials - 10Y is the only safe option
  { materialType: 'concrete', popularSize: '10Y', percentage: 94, bookingsThisMonth: 156 },
  { materialType: 'asphalt', popularSize: '10Y', percentage: 89, bookingsThisMonth: 78 },
  { materialType: 'dirt_soil', popularSize: '10Y', percentage: 92, bookingsThisMonth: 134 },
  { materialType: 'rock_gravel', popularSize: '10Y', percentage: 87, bookingsThisMonth: 45 },

  // C&D materials - 20Y is most popular
  { materialType: 'mixed_cd', popularSize: '20Y', percentage: 68, bookingsThisMonth: 243 },
  { materialType: 'wood', popularSize: '20Y', percentage: 61, bookingsThisMonth: 89 },
  { materialType: 'drywall', popularSize: '20Y', percentage: 72, bookingsThisMonth: 54 },
  { materialType: 'roofing', popularSize: '30Y', percentage: 58, bookingsThisMonth: 91 },

  // Household/light materials - varies by project
  { materialType: 'household', popularSize: '20Y', percentage: 55, bookingsThisMonth: 187 },
  { materialType: 'furniture', popularSize: '20Y', percentage: 49, bookingsThisMonth: 67 },
  { materialType: 'green_waste', popularSize: '20Y', percentage: 52, bookingsThisMonth: 78 },

  // Recycling
  { materialType: 'metal', popularSize: '10Y', percentage: 64, bookingsThisMonth: 34 },
  { materialType: 'cardboard', popularSize: '20Y', percentage: 58, bookingsThisMonth: 41 },
];

/**
 * Mock data - Regional activity by ZIP code
 * Simulates recent booking activity
 */
const REGIONAL_ACTIVITY: RegionalActivity[] = [
  { zipCode: '93401', ordersThisMonth: 127, trending: true },
  { zipCode: '93420', ordersThisMonth: 89, trending: false },
  { zipCode: '93422', ordersThisMonth: 156, trending: true },
  { zipCode: '93430', ordersThisMonth: 45, trending: false },
  { zipCode: '93405', ordersThisMonth: 98, trending: true },
  { zipCode: '93433', ordersThisMonth: 67, trending: false },
  { zipCode: '93442', ordersThisMonth: 134, trending: true },
  { zipCode: '93402', ordersThisMonth: 112, trending: false },
];

/**
 * Mock data - Material usage statistics
 * Shows which materials are trending
 */
const MATERIAL_USAGE: MaterialUsageStats[] = [
  { materialType: 'mixed_cd', usageCount: 243, percentageOfTotal: 28, trending: true },
  { materialType: 'concrete', usageCount: 156, percentageOfTotal: 18, trending: false },
  { materialType: 'household', usageCount: 187, percentageOfTotal: 22, trending: true },
  { materialType: 'dirt_soil', usageCount: 134, percentageOfTotal: 16, trending: false },
  { materialType: 'roofing', usageCount: 91, percentageOfTotal: 11, trending: true },
  { materialType: 'wood', usageCount: 89, percentageOfTotal: 10, trending: false },
  { materialType: 'green_waste', usageCount: 78, percentageOfTotal: 9, trending: false },
  { materialType: 'asphalt', usageCount: 78, percentageOfTotal: 9, trending: false },
];

/**
 * Mock data - Provider trust signals
 */
const PROVIDER_TRUST: Record<string, ProviderTrust> = {
  'paso-rolloff': {
    providerId: 'paso-rolloff',
    rating: 4.8,
    totalReviews: 342,
    verified: true,
    yearsInBusiness: 15,
    certificationsCount: 4,
  },
  'test-provider': {
    providerId: 'test-provider',
    rating: 4.6,
    totalReviews: 156,
    verified: true,
    yearsInBusiness: 8,
    certificationsCount: 2,
  },
};

/**
 * Social Proof Service
 *
 * Provides stateless functions to fetch social proof data for the booking flow.
 * All functions accept settings as a parameter to enable pure, testable functions.
 *
 * This follows the same pattern as MaterialRules - a const object with pure functions.
 */
export const SocialProofService = {
  /**
   * Get the most popular container size for a material type
   * Returns null if below threshold or disabled
   */
  getPopularContainer(
    materialType: string,
    settings: SocialProofSettings
  ): PopularContainer | null {
    if (!settings.enabled || !settings.showPopularityBadges) {
      return null;
    }

    const popular = POPULAR_CONTAINERS.find(p => p.materialType === materialType);

    if (!popular) {
      return null;
    }

    // Don't show if below thresholds
    if (
      popular.bookingsThisMonth < settings.minimumOrdersThreshold ||
      popular.percentage < settings.minimumPercentageThreshold
    ) {
      return null;
    }

    return popular;
  },

  /**
   * Get regional activity for a ZIP code
   * Returns null if below threshold or disabled
   */
  getRegionalActivity(
    zipCode: string,
    settings: SocialProofSettings
  ): RegionalActivity | null {
    if (!settings.enabled || !settings.showRegionalActivity) {
      return null;
    }

    // Match ZIP code (handle different formats)
    const cleanZip = zipCode.trim().substring(0, 5);
    const activity = REGIONAL_ACTIVITY.find(a => a.zipCode === cleanZip);

    if (!activity) {
      return null;
    }

    // Don't show if below threshold
    if (activity.ordersThisMonth < settings.minimumOrdersThreshold) {
      return null;
    }

    return activity;
  },

  /**
   * Get provider trust signals
   * Returns null if disabled
   */
  getProviderTrust(
    providerId: string,
    settings: SocialProofSettings
  ): ProviderTrust | null {
    if (!settings.enabled || !settings.showProviderTrust) {
      return null;
    }

    return PROVIDER_TRUST[providerId] || null;
  },

  /**
   * Get material usage statistics
   * Returns null if below threshold or disabled
   */
  getMaterialUsage(
    materialType: string,
    settings: SocialProofSettings
  ): MaterialUsageStats | null {
    if (!settings.enabled || !settings.showPopularityBadges) {
      return null;
    }

    const usage = MATERIAL_USAGE.find(m => m.materialType === materialType);

    if (!usage) {
      return null;
    }

    // Don't show if below threshold
    if (usage.percentageOfTotal < settings.minimumPercentageThreshold) {
      return null;
    }

    return usage;
  },

  /**
   * Get all popular materials sorted by usage
   * Useful for showing trending materials
   */
  getPopularMaterials(
    settings: SocialProofSettings,
    limit: number = 5
  ): MaterialUsageStats[] {
    if (!settings.enabled || !settings.showPopularityBadges) {
      return [];
    }

    return MATERIAL_USAGE
      .filter(m => m.percentageOfTotal >= settings.minimumPercentageThreshold)
      .sort((a, b) => b.usageCount - a.usageCount)
      .slice(0, limit);
  },

  /**
   * Check if social proof should be shown at all
   */
  isEnabled(settings: SocialProofSettings): boolean {
    return settings.enabled;
  },
} as const;

// Export for backward compatibility with existing code
export const socialProofService = SocialProofService;
